import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { SketchPicker } from "react-color";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import StoreIcon from "@mui/icons-material/Store";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import { createSetting, getAllSettings } from "../../service/settingService";
import { getAllBranchLists } from "../../service/branchService";
import { applyTheme } from "../../utils/applyTheme";
import { useTheme, useMediaQuery, alpha } from "@mui/material";
import { useSnackbar } from "../../App";
import Color from "../../components/Color";

const defaultColors = ["#21A1D3", "#D0021B", "#7C0BCC", "#CECCCCEE", "#9E9E9E", "#757575"];

const ColorPicker = ({ value, onChange, openPicker, onOpen, onClose, pickerKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const pickerRef = React.useRef(null);

  const handleColorChange = (color) => {
    onChange(typeof color === "string" ? color : color.hex);
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (openPicker === pickerKey) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openPicker, pickerKey, onClose]);

  return (
    <Box sx={{ position: "relative" }} ref={pickerRef}>
      <Grid container spacing={isMobile ? 0.5 : 1} alignItems="center">
        {defaultColors.map((color, idx) => (
          <Grid item key={idx} xs={2} sm="auto">
            <Tooltip title={color} arrow>
              <Box
                sx={{
                  width: isMobile ? 28 : 30,
                  height: isMobile ? 28 : 30,
                  borderRadius: "10px",
                  bgcolor: color,
                  cursor: "pointer",
                  border: value === color ? "3px solid #d7d7d7" : "2px solid #f0f0f0",
                  transition: "all 0.25s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => handleColorChange(color)}
              >
                {value === color && (
                  <CheckCircleIcon sx={{ color: "#fff", fontSize: isMobile ? 16 : 18 }} />
                )}
              </Box>
            </Tooltip>
          </Grid>
        ))}
        <Grid item xs={2} sm="auto">
          <Tooltip title="Pick Custom Color" arrow>
            <Box
              sx={{
                width: isMobile ? 32 : 34,
                height: isMobile ? 32 : 34,
                borderRadius: "10px",
                background: value || "#fafafa",
                cursor: "pointer",
                border: "2px solid transparent",
                backgroundImage: `linear-gradient(white, white), linear-gradient(135deg, #21A1D3, #7C0BCC)`,
                backgroundOrigin: "border-box",
                backgroundClip: "content-box, border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isMobile ? 14 : 16,
              }}
              onClick={() => onOpen(pickerKey)}
            >
              🎨
            </Box>
          </Tooltip>
        </Grid>
        <Grid item xs={2} sm="auto">
          <Tooltip title="Current Selected Color" arrow>
            <Box
              sx={{
                width: isMobile ? 32 : 34,
                height: isMobile ? 32 : 34,
                borderRadius: "50%",
                bgcolor: value || "transparent",
                border: "2px solid #ddd",
                ml: 1,
                boxShadow: value ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
              }}
            />
          </Tooltip>
        </Grid>
      </Grid>

      <Fade in={openPicker === pickerKey}>
        <Box
          sx={{
            mt: isMobile ? 0.5 : 1,
            position: "absolute",
            zIndex: 1300,
            borderRadius: 3,
            boxShadow: "0px 8px 24px rgba(0,0,0,0.25)",
            overflow: "hidden",
            border: "1px solid #eee",
            width: isMobile ? "180px" : "220px",
            left: isMobile ? "-50px" : 0,
          }}
        >
          <SketchPicker
            width={isMobile ? "180px" : "220px"}
            color={value || "#ffffff"}
            onChangeComplete={(color) => handleColorChange(color.hex)}
          />
        </Box>
      </Fade>
    </Box>
  );
};

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();

  const [branches, setBranches] = useState([]);
  const [allSettings, setAllSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(true);

  const [formData, setFormData] = useState({
    company_name: "",
    company_logo: null,
    gst_number: "",
    company_phone_number: "",
    billing_address: "",
    website_primary_color: "",
    website_secondary_color: "",
    invoice_primary_color: "",
    invoice_secondary_color: "",
    invoice_footer: "",
    invoice_template: "4", // ← taken from your API example
  });

  const [logoPreview, setLogoPreview] = useState("");

  const [branchSlots, setBranchSlots] = useState([
    { branchId: "", gst: "", address: "", phone: "", qrFile: null, qrPreview: "", qrUrl: "" },
  ]);

  const [loading, setLoading] = useState(false);
  const [openPicker, setOpenPicker] = useState(null);

  const branchOrdinals = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];

  const handleAddBranch = () => {
    setBranchSlots((prev) => [
      ...prev,
      { branchId: "", gst: "", address: "", phone: "", qrFile: null, qrPreview: "", qrUrl: "" },
    ]);
  };

  // Fetch branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await getAllBranchLists();
        let branchList = res?.data?.data || res?.data || [];
        if (!Array.isArray(branchList)) branchList = [];

        setBranches(branchList);
      } catch (err) {
        console.error("Fetch branches error:", err);
        showSnackbar("Failed to fetch branches", "error");
      }
    };
    fetchBranches();
  }, [showSnackbar]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoadingSettings(true);
      const res = await getAllSettings("invoice");
      if (res?.data?.data?.invoice) {
        const settingsObj = res.data.data.invoice;

        const logoURL = settingsObj.company_logo?.startsWith("http")
          ? settingsObj.company_logo
          : `${process.env.REACT_APP_API_BASE_URL || ""}${settingsObj.company_logo}`;

        const updated = { ...settingsObj, company_logo: logoURL };
        setAllSettings(updated);
        localStorage.setItem("settings", JSON.stringify(updated));
        applyTheme(updated);

        applyGlobalFields(updated);
        extractAndFillBranches(updated);
      }
    } catch (err) {
      showSnackbar("Failed to load settings", "error");
      const saved = localStorage.getItem("settings");
      if (saved) {
        const parsed = JSON.parse(saved);
        setAllSettings(parsed);
        applyTheme(parsed);
        applyGlobalFields(parsed);
        extractAndFillBranches(parsed);
      }
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const applyGlobalFields = useCallback((settings) => {
    setFormData({
      company_name: settings.company_name || "",
      gst_number: settings.gst_number || "",
      company_phone_number: settings.company_phone_number || "",
      billing_address: settings.billing_address || "",
      website_primary_color: settings.website_primary_color || "",
      website_secondary_color: settings.website_secondary_color || "",
      invoice_primary_color: settings.invoice_primary_color || "",
      invoice_secondary_color: settings.invoice_secondary_color || "",
      invoice_footer: settings.invoice_footer || "",
      invoice_template: settings.invoice_template || "4",
      company_logo: null,
    });
    setLogoPreview(settings.company_logo || "");
  }, []);

  const extractAndFillBranches = useCallback((settings) => {
    const branchMap = {};

    Object.keys(settings).forEach((key) => {
      const match = key.match(/^(branch_gst_number|branch_billing_address|branch_phone_number|branch_qrcode)_(\d+)$/);
      if (match) {
        const branchId = match[2];
        if (!branchMap[branchId]) branchMap[branchId] = { gst: "", address: "", phone: "", qrUrl: "" };

        if (key.includes("gst")) branchMap[branchId].gst = settings[key] || "";
        if (key.includes("billing")) branchMap[branchId].address = settings[key] || "";
        if (key.includes("phone")) branchMap[branchId].phone = settings[key] || "";
        if (key.includes("qrcode")) {
          branchMap[branchId].qrUrl = settings[key]?.startsWith("http")
            ? settings[key]
            : `${process.env.REACT_APP_API_BASE_URL || ""}${settings[key]}`;
        }
      }
    });

    const sortedIds = Object.keys(branchMap)
      .map(id => parseInt(id))
      .sort((a, b) => a - b)
      .map(String);

    const newSlots = sortedIds.map((branchId) => ({
      branchId,
      gst: branchMap[branchId]?.gst || "",
      address: branchMap[branchId]?.address || "",
      phone: branchMap[branchId]?.phone || "",
      qrFile: null,
      qrPreview: branchMap[branchId]?.qrUrl || "",
      qrUrl: branchMap[branchId]?.qrUrl || "",
    }));

    if (newSlots.length === 0) {
      newSlots.push({ branchId: "", gst: "", address: "", phone: "", qrFile: null, qrPreview: "", qrUrl: "" });
    }

    setBranchSlots(newSlots);
  }, []);

  const handleOpenPicker = (key) => setOpenPicker((prev) => (prev === key ? null : key));

  const handleGlobalChange = (field, value) => {
    let limited = value;
    if (field === "company_name") limited = value.slice(0, 100);
    if (field === "billing_address") limited = value.slice(0, 250);
    if (field === "invoice_footer") limited = value.slice(0, 500);
    setFormData((prev) => ({ ...prev, [field]: limited }));
  };

  const handleBranchSelect = (slotIndex, branchId) => {
    const alreadyUsed = branchSlots.some((s, i) => i !== slotIndex && s.branchId === branchId);
    if (alreadyUsed && branchId !== "") {
      showSnackbar("This branch is already selected!", "warning");
      return;
    }

    let gst = "";
    let address = "";
    let phone = "";
    let qrUrl = "";

    // 1. Try to find in existing saved settings
    if (branchId) {
      if (allSettings[`branch_gst_number_${branchId}`]) gst = allSettings[`branch_gst_number_${branchId}`];
      if (allSettings[`branch_billing_address_${branchId}`]) address = allSettings[`branch_billing_address_${branchId}`];
      if (allSettings[`branch_phone_number_${branchId}`]) phone = allSettings[`branch_phone_number_${branchId}`];
      if (allSettings[`branch_qrcode_${branchId}`]) {
        const path = allSettings[`branch_qrcode_${branchId}`];
        qrUrl = path.startsWith("http") ? path : `${process.env.REACT_APP_API_BASE_URL || ""}${path}`;
      }

      // 2. Fallback to Branch Entity details if no settings found
      if (!gst && !address && !phone) {
        const branchObj = branches.find((b) => String(b.id) === String(branchId));
        if (branchObj) {
          if (branchObj.gst_number) gst = branchObj.gst_number;
          if (branchObj.address || branchObj.billing_address) address = branchObj.address || branchObj.billing_address;
          if (branchObj.phone || branchObj.phone_number) phone = branchObj.phone || branchObj.phone_number;
        }
      }
    }

    setBranchSlots((prev) => {
      const updated = [...prev];
      updated[slotIndex] = {
        ...updated[slotIndex],
        branchId,
        gst,
        address,
        phone,
        qrFile: null,
        qrPreview: qrUrl,
        qrUrl,
      };
      return updated;
    });
  };

  const handleBranchChange = (slotIndex, field, value) => {
    setBranchSlots((prev) => {
      const updated = [...prev];
      updated[slotIndex] = { ...updated[slotIndex], [field]: value };
      return updated;
    });
  };

  const handleFileChange = (type, file, slotIndex = null) => {
    if (!file || !file.type.startsWith("image/")) {
      showSnackbar("Please select an image file", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar("Image size must be < 5MB", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (type === "logo") {
        setFormData((prev) => ({ ...prev, company_logo: file }));
        setLogoPreview(reader.result);
        window.dispatchEvent(new CustomEvent("logoPreviewUpdated", { detail: reader.result }));
      } else if (type === "qr" && slotIndex != null) {
        handleBranchChange(slotIndex, "qrFile", file);
        handleBranchChange(slotIndex, "qrPreview", reader.result);
        handleBranchChange(slotIndex, "qrUrl", "");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = (type, slotIndex = null) => {
    if (type === "logo") {
      setFormData((prev) => ({ ...prev, company_logo: null }));
      setLogoPreview("");
      // When logo is removed, we might want to reset sidebar to default or something
      // For now, just clear the preview
      window.dispatchEvent(new CustomEvent("logoPreviewUpdated", { detail: "" }));
    } else if (type === "qr" && slotIndex != null) {
      handleBranchChange(slotIndex, "qrFile", null);
      handleBranchChange(slotIndex, "qrPreview", "");
      handleBranchChange(slotIndex, "qrUrl", "");
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const data = new FormData();
      data.append("group", "invoice");

      if (formData.company_logo instanceof File) {
        data.append("logo", formData.company_logo);
      }

      Object.entries(formData).forEach(([key, value]) => {
        if (key !== "company_logo" && value != null && value !== "") {
          data.append(`settings[${key}]`, value);
        }
      });

      branchSlots.forEach((slot, index) => {
        if (slot.branchId) {
          data.append(`branch_ids_${index}`, slot.branchId);
          if (slot.gst) data.append(`branch_gst_numbers_${index}`, slot.gst);
          if (slot.address) data.append(`branch_billing_addresses_${index}`, slot.address);
          if (slot.phone) data.append(`branch_phone_numbers_${index}`, slot.phone);
          if (slot.qrFile instanceof File) {
            data.append(`branch_qrcodes_${index}`, slot.qrFile);
          }
        }
      });

      const res = await createSetting(data);
      const updated = res?.data?.data?.invoice || res?.data?.data || {};

      showSnackbar("Settings updated successfully", "success");

      const fullUpdated = { ...allSettings, ...updated };

      if (updated.company_logo) {
        fullUpdated.company_logo = updated.company_logo.startsWith("http")
          ? updated.company_logo
          : `${process.env.REACT_APP_API_BASE_URL || ""}${updated.company_logo}`;
        setLogoPreview(fullUpdated.company_logo);
      }

      // Update QR URLs if returned
      Object.keys(updated).forEach((key) => {
        if (key.startsWith("branch_qrcode_") && updated[key]) {
          fullUpdated[key] = updated[key].startsWith("http")
            ? updated[key]
            : `${process.env.REACT_APP_API_BASE_URL || ""}${updated[key]}`;
        }
      });

      setAllSettings(fullUpdated);
      localStorage.setItem("settings", JSON.stringify(fullUpdated));
      applyTheme(fullUpdated);
      window.dispatchEvent(new CustomEvent("settingsUpdated", { detail: fullUpdated }));

      applyGlobalFields(fullUpdated);
      extractAndFillBranches(fullUpdated);
    } catch (err) {
      if (err.response?.data?.errors) {
        Object.values(err.response.data.errors).flat().forEach((msg) => showSnackbar(msg, "error"));
      } else {
        showSnackbar("Failed to update settings", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Reset changes and reload from server?")) {
      localStorage.removeItem("settings");
      fetchSettings();
    }
  };

  if (loadingSettings) {
    return (
      <Box p={isMobile ? 1 : 0} mt={isMobile ? 2 : 1.5}>
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 4 }}>
          <Typography>Loading settings...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", m: 0, p: 0,  }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          px: 0,
          py: { xs: 4, sm: 4, md: 2 },
          borderRadius: 0,
          background: "#f0f1f7",
          minHeight: "100vh",
        }}
      >
        <Typography variant="h5" fontWeight={600} mb={2} fontSize={{ xs: 20, sm: 24 }}>
          Settings
        </Typography>

        {/* --- Company Identity Section --- */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: Color.secondary.main, letterSpacing: 1.2, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon fontSize="small" /> Company Identity
          </Typography>

          <Box sx={{
            py: { xs: 2, sm: 2 },
            px: 0,
            borderRadius: "24px",
            bgcolor: alpha(Color.secondary.main, 0.02),
            border: `1px solid ${alpha(Color.secondary.main, 0.08)}`
          }}>
            <Typography variant="body2" sx={{ px: 4, mb: 1.5, fontWeight: 600, color: "text.secondary" }}>Company Name</Typography>
            <Box sx={{ px: 4 }}>
              <TextField
                fullWidth
                value={formData.company_name}
                onChange={(e) => handleGlobalChange("company_name", e.target.value)}
                placeholder="e.g. Sri Vinayaka Murugan Sweets"
                helperText={`${formData.company_name.length}/100`}
                inputProps={{ maxLength: 100 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    bgcolor: "#fff",
                    "& fieldset": { borderColor: alpha(Color.secondary.main, 0.1) }
                  }
                }}
              />
            </Box>

            <Typography variant="body2" sx={{ px: 4, mt: 2, mb: 1.5, fontWeight: 600, color: "text.secondary" }}>Brand Identity Asset</Typography>
            <Box sx={{ px: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={logoPreview ? 7 : 12} sx={{ display: "flex", justifyContent: "center" }}>
                  <Box
                    component="label"
                    sx={{
                      width: 130,
                      height: 130,
                      border: `2px dashed ${alpha(Color.secondary.main, 0.2)}`,
                      borderRadius: "20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      bgcolor: "#fff",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": { background: alpha(Color.secondary.main, 0.01), borderColor: Color.secondary.main, transform: "scale(1.01)" }
                    }}
                  >
                    <Box sx={{ p: 1, borderRadius: "50%", bgcolor: alpha(Color.secondary.main, 0.05), display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <StoreIcon sx={{ color: Color.secondary.main }} />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: Color.secondary.main }}>
                      {logoPreview ? "UPDATE LOGO" : "UPLOAD BRAND LOGO"}
                    </Typography>
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileChange("logo", e.target.files[0])}
                    />
                  </Box>
                </Grid>
                {logoPreview && (
                  <Grid item xs={12} md={5} sx={{ display: "flex", justifyContent: "center" }}>
                    <Box sx={{
                      width: 130,
                      height: 130,
                      borderRadius: "20px",
                      p: 1.5,
                      border: "1px solid #eee",
                      bgcolor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.03)"
                    }}>
                      <img src={logoPreview} alt="Logo Preview" style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* --- Company Finance Section --- */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: Color.secondary.main, letterSpacing: 1.2, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon fontSize="small" /> Billing & Financial Details
          </Typography>

          <Box sx={{
            py: { xs: 2, sm: 2 },
            px: 0,
            borderRadius: "24px",
            bgcolor: alpha(Color.secondary.main, 0.02),
            border: `1px solid ${alpha(Color.secondary.main, 0.08)}`,
            width: "100%",
            boxSizing: "border-box"
          }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="body2" sx={{ px: 4, mb: 1, fontWeight: 700, color: "text.secondary" }}>GST Number</Typography>
                <Box sx={{ px: 4 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.gst_number}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^A-Z0-9]/gi, "").toUpperCase();
                      if (v.length <= 15) handleGlobalChange("gst_number", v);
                    }}
                    inputProps={{ maxLength: 15 }}
                    helperText={`${formData.gst_number.length}/15`}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        transition: "all 0.3s ease",
                        "&:hover fieldset": { borderColor: Color.secondary.main }
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ width: "100%" }}>
                <Typography variant="body2" sx={{ px: 4, mb: 1, fontWeight: 700, color: "text.secondary" }}>Contact Phone</Typography>
                <Box sx={{ px: 4 }}>
                  <TextField
                    fullWidth
                    placeholder="10-digit number"
                    InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, fontSize: 18, opacity: 0.4 }} /> }}
                    value={formData.company_phone_number}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 10) handleGlobalChange("company_phone_number", val);
                    }}
                    inputProps={{ maxLength: 10 }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        transition: "all 0.3s ease"
                      }
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ width: "100%" }}>
                <Typography variant="body2" sx={{ px: 4, mb: 1, fontWeight: 700, color: "text.secondary" }}>Primary Billing Address</Typography>
                <Box sx={{ px: 4 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    value={formData.billing_address}
                    onChange={(e) => handleGlobalChange("billing_address", e.target.value)}
                    placeholder="Street, City, Pin, etc..."
                    helperText={`${formData.billing_address.length}/250`}
                    inputProps={{ maxLength: 250 }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "18px",
                        bgcolor: "#fff",
                        transition: "all 0.3s ease"
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ────────────────────────────────────────────────
            Branch Sections (3 slots)
        ──────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            width: "100%",
            justifyContent: "stretch",
            mt: 2,
            mb: 4
          }}
        >
          {branchSlots.map((slot, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                px: { xs: 2.5, sm: 3.5, md: 4 },
                py: { xs: 4, sm: 5 },
                borderRadius: "32px",
                border: `1px solid ${alpha(Color.secondary.main, 0.1)}`,
                height: "100%",
                background: `linear-gradient(135deg, #ffffff 0%, ${alpha(Color.secondary.main, 0.02)} 100%)`,
                boxShadow: "0 10px 40px rgba(0,0,0,0.03)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                overflow: "hidden",
                boxSizing: "border-box",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: `0 20px 50px ${alpha(Color.secondary.main, 0.08)}`,
                  borderColor: alpha(Color.secondary.main, 0.2),
                }
              }}
            >
              {/* Accent glass bubble */}
              <Box sx={{
                position: "absolute",
                top: -20,
                right: -20,
                width: 100,
                height: 100,
                background: `radial-gradient(circle, ${alpha(Color.secondary.main, 0.05)} 0%, transparent 70%)`,
                borderRadius: "50%",
              }} />

              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item>
                  <Box sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "14px",
                    background: alpha(Color.secondary.main, 0.1),
                    color: Color.secondary.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 8px 16px ${alpha(Color.secondary.main, 0.1)}`
                  }}>
                    <StoreIcon />
                  </Box>
                </Grid>
                <Grid item xs>
                  <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ letterSpacing: "-0.5px" }}>
                    {branchOrdinals[index] || `Branch ${index + 1}`} Branch Details
                  </Typography>
                  {/* <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Configure local distribution hub settings
                  </Typography> */}
                </Grid>
              </Grid>

              <FormControl fullWidth sx={{ mb: 2, mt:1 }}>
                <InputLabel>Select Linked Branch</InputLabel>
                <Select
                  value={slot.branchId}
                  onChange={(e) => handleBranchSelect(index, e.target.value)}
                  label="Select Linked Branch"
                  sx={{ borderRadius: "12px", bgcolor: "#fff" }}
                >
                  <MenuItem value=""><em>-- Choose Branch --</em></MenuItem>
                  {branches.map((b) => {
                    const disabled = branchSlots.some((s, i) => i !== index && s.branchId === String(b.id));
                    return (
                      <MenuItem key={b.id} value={String(b.id)} disabled={disabled}>
                        {b.name || b.branch_name || "Outlet"} (ID: {b.id})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {slot.branchId && (
                <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
                  <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Regional GSTIN"
                        fullWidth
                        size="small"
                        value={slot.gst}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^A-Z0-9]/gi, "").toUpperCase();
                          if (v.length <= 15) handleBranchChange(index, "gst", v);
                        }}
                        inputProps={{ maxLength: 15 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Contact Number"
                        fullWidth
                        size="small"
                        value={slot.phone}
                        InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, fontSize: 18, opacity: 0.4 }} /> }}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          if (val.length <= 10) handleBranchChange(index, "phone", val);
                        }}
                        inputProps={{ maxLength: 10 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: "text.secondary", display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 4, height: 16, bgcolor: Color.secondary.main, borderRadius: 1, mr: 1 }} />
                      Terminal QR & Identity
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={slot.qrPreview || slot.qrUrl ? 6 : 12} sx={{ display: "flex", justifyContent: "center" }}>
                        <Box
                          component="label"
                          sx={{
                            width: 120,
                            height: 120,
                            border: `2px dashed ${alpha(Color.secondary.main, 0.2)}`,
                            borderRadius: "16px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            bgcolor: alpha(Color.secondary.main, 0.01),
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": { bgcolor: alpha(Color.secondary.main, 0.03), borderColor: Color.secondary.main, transform: "scale(1.01)" }
                          }}
                        >
                          <BusinessIcon sx={{ opacity: 0.5 }} />
                          <Typography variant="caption" fontWeight={700}>
                            {slot.qrPreview || slot.qrUrl ? "Replace QR" : "Upload QR"}
                          </Typography>
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => handleFileChange("qr", e.target.files[0], index)}
                          />
                        </Box>
                      </Grid>

                      {(slot.qrPreview || slot.qrUrl) && (
                        <Grid item xs={12} lg={6} sx={{ display: "flex", justifyContent: "center" }}>
                          <Box sx={{
                            width: 120,
                            height: 120,
                            p: 1.5,
                            borderRadius: "16px",
                            border: "1px solid #eee",
                            bgcolor: "#fff",
                            display: "flex",
                            justifyContent: "center",
                            overflow: "hidden",
                            boxShadow: "inset 0 2px 8px rgba(0,0,0,0.02)"
                          }}>
                            <img
                              src={slot.qrPreview || slot.qrUrl}
                              alt="QR Preview"
                              style={{ maxHeight: "100%", width: "auto", objectFit: "contain" }}
                            />
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  <Box>
                    <TextField
                      label="Branch Mailing Address"
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      value={slot.address}
                      InputProps={{
                        startAdornment: <LocationOnIcon sx={{ mr: 1, mt: 0.5, fontSize: 18, opacity: 0.4, alignSelf: "flex-start" }} />,
                      }}
                      onChange={(e) => handleBranchChange(index, "address", e.target.value)}
                      inputProps={{ maxLength: 200 }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* Add More Branch Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddBranch}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              px: 3,
              py: 1,
              backgroundColor: Color.secondary.main,
              color: "white",
              "&:hover": {
                backgroundColor: Color.secondary.main,
                opacity: 0.9
              }
            }}
          >
            Add More Branch
          </Button>
        </Box>

        {/* Theme */}
        <Typography variant="h6" mt={5} mb={1}>Dashboard Theme</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1}>Primary Color</Typography>
            <ColorPicker
              value={formData.website_primary_color}
              onChange={(c) => handleGlobalChange("website_primary_color", c)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              onClose={() => setOpenPicker(null)}
              pickerKey="website_primary"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1}>Secondary Color</Typography>
            <ColorPicker
              value={formData.website_secondary_color}
              onChange={(c) => handleGlobalChange("website_secondary_color", c)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              onClose={() => setOpenPicker(null)}
              pickerKey="website_secondary"
            />
          </Grid>
        </Grid>

        {/* Invoice Footer + Colors */}
        <Typography variant="h6" mt={5} mb={1}>Invoice Footer</Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          value={formData.invoice_footer}
          onChange={(e) => handleGlobalChange("invoice_footer", e.target.value)}
          placeholder="Thank you for your business..."
          helperText={`${formData.invoice_footer.length}/500`}
          inputProps={{ maxLength: 500 }}
          sx={{ mb: 3 }}
        />

        {/* <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1}>Invoice Primary Color</Typography>
            <ColorPicker
              value={formData.invoice_primary_color}
              onChange={(c) => handleGlobalChange("invoice_primary_color", c)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              onClose={() => setOpenPicker(null)}
              pickerKey="invoice_primary"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1}>Invoice Secondary Color</Typography>
            <ColorPicker
              value={formData.invoice_secondary_color}
              onChange={(c) => handleGlobalChange("invoice_secondary_color", c)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              onClose={() => setOpenPicker(null)}
              pickerKey="invoice_secondary"
            />
          </Grid>
        </Grid> */}

        {/* Buttons */}
        <Grid container spacing={2} mt={5} justifyContent="flex-end">
          <Grid item xs={12} sm={3}>
            <Button fullWidth variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Settings;